/* eslint-disable no-unused-vars */
import React, {
    createContext,
    useContext,
    useReducer,
    useEffect,
    useState,
    useRef,
    useMemo
} from 'react';
import PropTypes from 'prop-types';
import appManager from 'AppManager';
import { AutoParentalLockService } from 'Utils/autoParentalLockService';
import { getDeviceId } from 'DeviceUtils';
import { parseToken } from '@24i/st-api-connector/utils';
import { cookieAllowed, CookieTypes } from '../utils/cookieUtils';
import deviceStorageReducer from './reducers/deviceStorageReducer';
import { DEFAULT_PARENTAL_CONTROL_SETTINGS, DEFAULT_LIST_MODE, EPG_LIST_TYPES } from '../options';
import storeActions from './storeActions';
import AnalyticsService from '../AnalyticsService';
// import usePlayerData from '../localGluons/components/PlayerContainer/hooks/usePlayerData';
const isValidState = (state) => state && Object.entries(state).length !== 0;

// eslint-disable-next-line max-statements
export const StoreProvider = ({ children }) => {
    const [deviceStorageState, deviceStorageDispatch] = useReducer(deviceStorageReducer, {});
    const [userData, setUserData] = useState(null);
    const [mCoreConfig, setMCoreConfig] = useState(null);
    const [playerData, setPlayerData] = useState(null);
    const [language, setLanguage] = useState(null);
    const previousLanguage = useRef(language);
    const [categories, setCategories] = useState([]);
    const [parentalLockConfig, setParentalLockConfig] = useState(DEFAULT_PARENTAL_CONTROL_SETTINGS);
    const [packageFilter, setPackageFilter] = useState(0);
    const [epgSelectedListMode, setEpgSelectedListMode] = useState('');
    const [epgFilters, setEpgFilters] = useState([]);
    const [epgFilteredChannels, setEpgFilteredChannels] = useState([]);
    const [epgCategories, setEpgCategories] = useState([]);
    const isIPTVAvailable = useMemo(() => userData?.platforms.includes('IPTV'), [userData]);
    const [lastPlayerBrightness, setLastPlayerBrightness] = useState(null);
    const [isVideoBlocked, setIsVideoBlocked] = useState(false);
    const [mySubscriptions, setMySubscriptions] = useState(null);
    const [continueWatchingEnabled, setContinueWatchingEnabled] = useState(false);
    const [isGracePeriod, setIsGracePeriod] = useState(false);
    const progressInfo = useRef(null);
    const [hideLoginHeader, setHideLoginHeader] = useState(false);
    // const [maxBitRateValue, setMaxBitRateValue] =useState(playerInstance?.trackRestrictions.maxBitrate ?? Infinity );
    const [dataSaverValue, setDataSaverValue] = useState(
        deviceStorageState.dataSaverValue ?? false
    );

    useEffect(() => {
        if (isValidState(deviceStorageState)) {
            const setDeviceConfigData = async () =>
                appManager.setDeviceConfigData({
                    ...deviceStorageState
                });
            setDeviceConfigData();
        }
    }, [deviceStorageState]);

    useEffect(() => {
        if (
            deviceStorageState.autoParentalLockInterval &&
            parentalLockConfig.enabled &&
            parentalLockConfig.unlocked
        ) {
            AutoParentalLockService.startTimeout(
                deviceStorageState.autoParentalLockInterval,
                () => {
                    setParentalLockConfig({
                        ...parentalLockConfig,
                        unlocked: false
                    });
                }
            );
        }
        return () => {
            AutoParentalLockService.clearTimeout();
        };
    }, [deviceStorageState.autoParentalLockInterval, parentalLockConfig.unlocked]);

    useEffect(() => {
        const getParentalSettings = async () => {
            // Get parental locking data only for logged in user
            if (userData) {
                const parentalLockMCoreConfig = await appManager.getApi().getParentalLocking();
                // Merge our and their config, our has also if control is temporary unlocked
                setParentalLockConfig({ ...parentalLockConfig, ...parentalLockMCoreConfig });
            }
        };

        getParentalSettings();
        if (cookieAllowed(CookieTypes.googleAnalytics))
            if (userData) {
                AnalyticsService.setUserId(userData.login);
            } else {
                const setUserId = async () => {
                    const deviceId = await getDeviceId();
                    AnalyticsService.setUserId(deviceId);
                };
                setUserId();
            }
    }, [userData]);

    useEffect(() => {
        const processBitRateSetup = async () => {
            if (language) {
                deviceStorageDispatch({ type: storeActions.setLanguage, payload: language });
                // Wipe cache on language cache to refresh titles etc. setup api again to prefetch data
                // And do not wipe cache after first language set
                if (language !== previousLanguage.current && previousLanguage.current !== null) {
                    await appManager.wipeCache();
                    await appManager.setupApi();
                }
                if (cookieAllowed(CookieTypes.googleAnalytics)) {
                    AnalyticsService.setUserLanguage(language);
                }
                previousLanguage.current = language;
            }
        };

        processBitRateSetup();
    }, [language]);

    useEffect(() => {
        const processEpgSelectedListModeChange = async () => {
            const storedEPGListMode = deviceStorageState.epgSelectedListMode;

            if (storedEPGListMode === undefined) return;
            if (storedEPGListMode === '' && !epgSelectedListMode) {
                setEpgSelectedListMode(DEFAULT_LIST_MODE);
                return;
            }
            if (storedEPGListMode && !epgSelectedListMode) {
                setEpgSelectedListMode(storedEPGListMode);
                return;
            }

            const mode =
                epgSelectedListMode === EPG_LIST_TYPES.FAVORITES ? epgSelectedListMode : '';

            deviceStorageDispatch({
                type: storeActions.setEpgSelectedListMode,
                payload: mode
            });
        };

        processEpgSelectedListModeChange();
    }, [epgSelectedListMode, deviceStorageState.epgSelectedListMode]);

    const onRefreshToken = async (refreshTokenData) => {
        const { token: newToken, tokenData: newTokenData } = refreshTokenData;
        const updatedLoggedUserData = { ...newTokenData, ...newToken.userInfo };
        const { phv: newPhv } = parseToken(newToken.accessToken);
        const loggedUserData = await appManager.getLoggedUserObjectDataFromStorage();

        if (!loggedUserData.accessToken) {
            // Not logged in or another issue
            return;
        }
        const { phv } = parseToken(loggedUserData.accessToken);

        if (phv === newPhv && mySubscriptions !== null) return;

        // fetch mySubscriptions
        const newSubscriptions = await appManager
            .getApi()
            .backstageConnector.home.getMySubscriptions();
        setMySubscriptions(newSubscriptions);
        appManager.setMySubscriptions(newSubscriptions);
        await appManager.updateTokenInStorage(newToken, newTokenData);
        setUserData(updatedLoggedUserData);
    };

    const initializeMySubscriptions = async () => {
        const storageMySubs = appManager.getMySubscriptions();
        if (storageMySubs) {
            setMySubscriptions(storageMySubs);
            return;
        }

        const newSubscriptions = await appManager
            .getApi()
            .backstageConnector.home.getMySubscriptions();

        setMySubscriptions(newSubscriptions);
        appManager.setMySubscriptions(newSubscriptions);
    };

    useEffect(() => {
        initializeMySubscriptions();
        if (appManager.api.backstageConnector.session.addRefreshTokenListener)
            appManager.api.backstageConnector.session.addRefreshTokenListener(onRefreshToken);

        return () => {
            appManager.api.backstageConnector.session.removeRefreshTokenListener(onRefreshToken);
        };
    }, []);

    useEffect(() => {
        const processDataSaverChanger = async () => {
            deviceStorageDispatch({
                type: storeActions.setDataSaverValue,
                payload: dataSaverValue
            });
        };
        processDataSaverChanger();
    }, [dataSaverValue]);

    return (
        <StoreContext.Provider
            value={{
                appManager,
                deviceStorageState,
                deviceStorageDispatch,
                userData,
                setUserData,
                isIPTVAvailable,
                language,
                setLanguage,
                playerData,
                setPlayerData,
                lastPlayerBrightness,
                saveLastPlayerBrightness: setLastPlayerBrightness,
                mCoreConfig,
                setMCoreConfig,
                categories,
                setCategories,
                parentalLockConfig,
                setParentalLockConfig,
                packageFilter,
                setPackageFilter,
                epgSelectedListMode,
                setEpgSelectedListMode,
                epgFilters,
                setEpgFilters,
                epgFilteredChannels,
                setEpgFilteredChannels,
                epgCategories,
                setEpgCategories,
                isVideoBlocked,
                setIsVideoBlocked,
                mySubscriptions,
                continueWatchingEnabled,
                setContinueWatchingEnabled,
                isGracePeriod,
                setIsGracePeriod,
                progressInfo,
                dataSaverValue,
                setDataSaverValue,
                hideLoginHeader,
                setHideLoginHeader
            }}
        >
            {children}
        </StoreContext.Provider>
    );
};

StoreProvider.defaultProps = {
    children: []
};

StoreProvider.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};

// TODO: Refactor into interface when refactoring store into TS
export const StoreContext = createContext({
    appManager,
    deviceStorageState: {},
    deviceStorageDispatch: (_dispatchOptions) => {},
    userData: {},
    setUserData: (_data) => {},
    isIPTVAvailable: Boolean,
    language: String,
    setLanguage: (_language) => {},
    playerData: {},
    setPlayerData: (_data) => {},
    lastPlayerBrightness: Number,
    saveLastPlayerBrightness: (_brightness) => {},
    mCoreConfig: {},
    setMCoreConfig: (_config) => {},
    categories: [],
    setCategories: (_array) => {},
    parentalLockConfig: {},
    setParentalLockConfig: (_config) => {},
    packageFilter: {},
    setPackageFilter: (_filter) => {},
    epgSelectedListMode: String,
    setEpgSelectedListMode: (_listmode) => {},
    epgFilters: {},
    setEpgFilters: (_filters) => {},
    epgFilteredChannels: {},
    setEpgFilteredChannels: (_channels) => {},
    epgCategories: [],
    setEpgCategories: (_array) => {},
    continueWatchingEnabled: false,
    setContinueWatchingEnabled: (_state) => {},
    isGracePeriod: false,
    setIsGracePeriod: (_boolean) => {},
    progressInfo: {},
    dataSaverValue: Boolean,
    setDataSaverValue: (_boolean) => {},
    mySubscriptions: {},
    setMySubscriptions: (_mySubscriptions) => {},
    hideLoginHeader: Boolean,
    setHideLoginHeader: (_hideLoginHeader) => {}
});

export const useStore = () => useContext(StoreContext);

export const withStore = (Component) => (props) => (
    <StoreContext.Consumer>
        {(storeContext) => <Component {...props} {...storeContext} />}
    </StoreContext.Consumer>
);
